import React, { useState, useEffect, useCallback, useRef } from "react";
import Print from "../api/print";
import { GridDataState } from '../utils/store';
import { useSelector } from 'react-redux';
import './styles.css'
import * as JSPM from "jsprintmanager";
import { AlertsData } from "./dashboard";
import NotifyComponent from "../components/notifyComponent";
import { DownloadJSPMDriver } from "../utils/downloadDrivers";
import { PrintExtraVehicleData } from "../api/printVehicle";
import { useRoleContext } from "../utils/roleContext";

interface Props {
    onAlertChnage: AlertsData;
    title: string
}

interface Template {
    name: string;
}

interface Printer {
    name: string;
}

const PrintTemplateSelector = ({ onAlertChnage, title }: Props) => {
    const [templateName, setTemplateName] = useState<string>('');
    const [templateData, setTemplateData] = useState<Template[]>([]);
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [error, setError] = useState<{ code?: number, message?: string }>({});
    const [executedOnce, setExecutedOnce] = useState<boolean>(false);
    const [printers, setPrinters] = useState<Printer[]>([]);
    const [selectedPrinter, setSelectedPrinter] = useState<string>('');
    const [showPrinterSelector, setShowPrinterSelector] = useState<boolean>(false);
    const [showDefaultDialogue, setShowDefaultDialogue] = useState<boolean>(true);
    const [pendingPrintJob, setPendingPrintJob] = useState<any>(null);
    const { userRole } = useRoleContext();
    let Did = sessionStorage.getItem('dealerId');
    const fetchPrintersRef = useRef(false);
    const gridData = useSelector((state: GridDataState) => state.gridData);
    let vin = gridData.gridData;

    const fetchData = useCallback(async (type: string, fileName: string) => {
        if (Did && type === 'getTemplateList') {
            let res = await Print(type, parseInt(Did), '', '');
            let resFileData = res.split(';');
            if (resFileData.length > 1) {
                setTemplateData(resFileData.map((name: any) => ({ name })))
            } else {
                setTemplateName(resFileData[0]);
            }
        } else if (type === 'getTemplateData') {
            const vins = Array.isArray(vin) ? vin : [vin];
            const data = await Promise.all(
                vins.map(async (element: string) => {
                    if (Did) {
                        return Print(type, parseInt(Did), element, fileName);
                    }
                })
            );

            return data;
        }
    }, [Did, vin])

    const jspmWSStatus = () => {
        if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Closed) {
            return false;
        } else if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open) {
            return true;
        } else if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Blocked) {
            return false;
        }
    }

    useEffect(() => {
        JSPM.JSPrintManager.license_url = process.env.REACT_APP_SERVER_URL + "JSPM/LicenseV7";
        JSPM.JSPrintManager.auto_reconnect = true;
        JSPM.JSPrintManager.start();
        setTimeout(() => {
            const isJSPMInstalled = jspmWSStatus();

            if (!isJSPMInstalled) {
                setError({ code: 3, message: 'No Driver found' });
                setShowNotification(true);
            } else {
                fetchData('getTemplateList', '');
            }
        }, 2000);
    }, [fetchData])

    const sendResponse = (errCode: number, errMsg: string, status: string, p0?: string) => {
        onAlertChnage(errCode, errMsg, status)
    }

    useEffect(() => {
        if (!executedOnce && templateName) {
            handlePrint();
            setExecutedOnce(true);
        }
    }, [templateName, executedOnce]);

    const executePrintJob = (printerName: string, zplFile: string) => {
        const printJob = new JSPM.ClientPrintJob();
        printJob.clientPrinter = new JSPM.InstalledPrinter(printerName)
        printJob.printerCommands = zplFile;
        sendResponse(0, "Print job started", "Success");
        printJob.sendToClient();
    }

    const handlePrint = async () => {
        if (title !== "ExtraVehPrint") {
            let resData = await fetchData('getTemplateData', templateName);
            resData?.map(res => {
                if (res.defaultPrinter !== null && userRole === 'Director') {
                    const savedPrinter = localStorage.getItem('defaultPrinter');
                    if (savedPrinter) {
                        executePrintJob(savedPrinter, res.zplFile);
                    }
                    setShowDefaultDialogue(false);
                    setShowPrinterSelector(true);
                    setPendingPrintJob({ zplFile: res.zplFile });
                } else {
                    if (res.defaultPrinter === null) {
                        sendResponse(1, "Please configure a printer in the preferences tab", "Failure");
                    } else {
                        executePrintJob(res.defaultPrinter, res.zplFile);
                    }
                }
            });
        } else {
            if (Did) {
                let resData = await PrintExtraVehicleData(JSON.stringify(vin), templateName, parseInt(Did))
                if (resData.defaultPrinter !== null && userRole === 'Director') {
                    const savedPrinter = localStorage.getItem('defaultPrinter');
                    if (savedPrinter) {
                        executePrintJob(savedPrinter, resData.zplFile);
                    }
                    setShowDefaultDialogue(false);
                    setShowPrinterSelector(true);
                    setPendingPrintJob({ zplFile: resData.zplFile })
                } else {
                    if (resData.defaultPrinter === null) {
                        sendResponse(1, "Please configure a printer in the preferences tab", "Failure");
                    } else {
                        executePrintJob(resData.defaultPrinter, resData.zplFile);
                    }
                }
            }
        }
    }

    const fetchPrinters = useCallback(async () => {
        if (JSPM.JSPrintManager.websocket_status === JSPM.WSStatus.Open && !fetchPrintersRef.current) {
            let printerNames: Printer[];
            printerNames = await JSPM.JSPrintManager.getPrinters() as Printer[];
            setPrinters(printerNames);
            fetchPrintersRef.current = true; // Mark fetchPrinters as called
        }
    }, []);

    if (!fetchPrintersRef.current) {
        fetchPrinters();
    }

    const handleOk = () => {
        setShowNotification(false);
        JSPM.JSPrintManager.stop();
        sendResponse(2, "Downloading jsPrintManager. After installing please refresh and try again", "Info");
        DownloadJSPMDriver();
    }

    const handleCancel = () => {
        setShowNotification(false);
        JSPM.JSPrintManager.stop();
        sendResponse(2, "Print cancelled as user didnt want to download the driver", "Info");
    }

    const handlePrinterSelection = () => {
        if (selectedPrinter && pendingPrintJob) {
            localStorage.setItem('defaultPrinter', selectedPrinter);
            executePrintJob(selectedPrinter, pendingPrintJob.zplFile);
            setPendingPrintJob(null);
            setShowPrinterSelector(false);
            setShowDefaultDialogue(true);
        } else {
            sendResponse(1, "Please select a printer", "Warning");
        }
    }

    return (
        <div>
            {templateData.length < 1 ? (
                <div className="modalTop-body mt-3">
                    {showDefaultDialogue && (
                        <p className="headerText text-center text-lg">Printing the file {templateName}</p>
                    )}
                    {showPrinterSelector && (
                        <div>
                            <div className="flex justify-between items-center">
                                <p className="headerText text-center text-lg">Please select a printer</p>
                                <select className="sampleLabel" value={selectedPrinter} onChange={(e) => { setSelectedPrinter(e.target.value) }} >
                                    <option value="">Select a printer</option>
                                    {printers.map((option: any) => {
                                        return (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="flex justify-around items-center mt-4">
                                <button className="button-6" onClick={handlePrinterSelection}>Print</button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <div className="modalTop-body mt-3">
                        <p className="headerText text-center text-lg">Select a file to print</p>
                    </div>
                    <div className="p-2 flex justify-center items-center modal-body">
                        {showDefaultDialogue && (
                            <div>
                                <div className="grid grid-cols-2 gap-2">
                                    {templateData.map(template => (
                                        <div key={template.name} className="items-center">
                                            <input
                                                type="radio"
                                                checked={templateName === template.name}
                                                value={template.name}
                                                onChange={(e) => setTemplateName(e.target.value)}
                                            />
                                            <label className="sampleLabel pl-2">{template.name}</label>
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-footer">
                                    <div className="flex justify-around items-center mt-4">
                                        <button className="button-6" onClick={handlePrint}>Print</button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {showPrinterSelector && (
                            <div>
                                <div className="flex justify-between items-center">
                                    <p className="headerText text-center text-lg">Please select a printer</p>
                                    <select className="sampleLabel" value={selectedPrinter} onChange={(e) => { setSelectedPrinter(e.target.value) }} >
                                        <option value="">Select a printer</option>
                                        {printers.map((option: any) => {
                                            return (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="flex justify-around items-center mt-4">
                                    <button className="button-6" onClick={handlePrinterSelection}>Print</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {error?.code === 3 ? (
                <NotifyComponent title="Printer Driver not found" message='Printer driver not found!.Do you want to download the driver ?' onOk={handleOk} onCancel={handleCancel} show={showNotification} />
            ) : null}
        </div>
    )
}

export default PrintTemplateSelector